import { useMemo, useCallback, useImperativeHandle, Ref } from "react";
import {
  ViewerFragmentCommonProps,
  ViewerFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import TitledTextArea from "@coworker/apprestructured/src/task-management/components/TitledTextArea/TitledTextArea";
import StatusBadge from "@coworker/apprestructured/src/task-management/components/StatusBadge/StatusBadge";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";

const MAX_TEXT_AREA_LENGTH = 1000;

export type TitledTextAreaViewerFragmentConfigProps = {
  field: string;
  title?: string;
  label: string;
  validationErrorTranslationKey?: string;
  statusIndicator?: {
    completedKey: string;
    closedKey: string;
  };
};

type TitledTextAreaViewerFragmentProps = {
  configProps: TitledTextAreaViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
  ref?: Ref<ViewerFragmentHandle>;
};

function TitledTextAreaViewerFragment({
  configProps,
  commonProps,
  ref,
}: TitledTextAreaViewerFragmentProps) {
  const {
    field,
    title,
    label,
    validationErrorTranslationKey,
    statusIndicator,
  } = configProps;
  const { task, onTaskChange, blockInput, showValidation } = commonProps;
  const state = getByPath(task, "state");
  const isTaskPending =
    !isLoading(task) && state !== "completed" && state !== "closed";
  const value = getTypedValue<string>(getByPath(task, field)) || "";

  const { t } = useTranslation();

  const isEmpty = !isLoading(value) && value.trim().length === 0;

  const error = useMemo(() => {
    if (!isLoading(value) && value.length > MAX_TEXT_AREA_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_TEXT_AREA_LENGTH,
      });
    }
    if (validationErrorTranslationKey && isEmpty && showValidation) {
      return t(validationErrorTranslationKey);
    }
    return null;
  }, [value, t, validationErrorTranslationKey, isEmpty, showValidation]);

  useImperativeHandle(ref, () => ({
    valid:
      !isLoading(value) &&
      !error &&
      !(validationErrorTranslationKey && isEmpty),
  }));

  const handleTextChange = useCallback(
    (newValue: string) => {
      if (typeof value !== "string") return;
      if (
        newValue.length <= MAX_TEXT_AREA_LENGTH ||
        newValue.length < value.length
      ) {
        onTaskChange(setByPath(field, newValue));
      }
    },
    [value, field, onTaskChange]
  );

  if (isLoading(value)) {
    return null;
  }

  const showStatusIndicator = !!statusIndicator && !isTaskPending;
  const header = showStatusIndicator ? (
    <StatusBadge variant={state === "completed" ? "positive" : "negative"}>
      {t(
        state === "completed"
          ? statusIndicator.completedKey
          : statusIndicator.closedKey
      )}
    </StatusBadge>
  ) : undefined;

  return (
    <TitledTextArea
      title={showStatusIndicator ? undefined : title}
      header={header}
      label={showStatusIndicator ? "" : label}
      ariaLabel={showStatusIndicator ? label : undefined}
      value={value}
      onChange={handleTextChange}
      error={error}
      disabled={blockInput || !isTaskPending}
    />
  );
}

export default TitledTextAreaViewerFragment;
